import React, { useEffect, useState } from "react";
import api, { fmtCLP, fmtDateShortCL } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Check, Clock, CircleDashed, FileText, Eye, Banknote, Handshake, Send, Trophy, Lock, Loader2, X } from "lucide-react";

const ICONS = {
  publication: Send,
  approval: Check,
  interest: Eye,
  documents: FileText,
  negotiation: Banknote,
  visit: Handshake,
  closure: Trophy,
};

const STATUS_STYLES = {
  done: {
    dot: "bg-emerald-500 text-white",
    line: "bg-emerald-300",
    badge: "bg-emerald-50 text-emerald-700 border-emerald-200",
    badgeText: "Completado",
    ring: "ring-emerald-100",
  },
  active: {
    dot: "bg-amber-500 text-white animate-pulse",
    line: "bg-zinc-200",
    badge: "bg-amber-50 text-amber-800 border-amber-200",
    badgeText: "En curso",
    ring: "ring-amber-100",
  },
  pending: {
    dot: "bg-white text-slate-400 border-2 border-zinc-300",
    line: "bg-zinc-200",
    badge: "bg-zinc-100 text-slate-500 border-zinc-200",
    badgeText: "Pendiente",
    ring: "ring-zinc-100",
  },
};

export default function ProcessTimeline({ businessId, investorId, compact = false }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    if (!businessId) return;
    setLoading(true);
    const qs = investorId ? `?investor_id=${investorId}` : "";
    return api.get(`/process/${businessId}${qs}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [businessId, investorId]);

  const decide = async (accessId, status) => {
    try {
      await api.patch(`/admin/document-access/${accessId}`, { status });
      toast.success(status === "approved" ? "Acceso a documentos aprobado" : "Acceso rechazado");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  if (loading) {
    return (
      <div className="bg-white border border-zinc-200 rounded-lg p-6 flex items-center justify-center text-slate-400 text-sm">
        <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Cargando proceso…
      </div>
    );
  }
  if (!data) return null;

  const stages = data.stages || [];
  const doneCount = stages.filter((s) => s.status === "done").length;
  const total = stages.length;
  const pct = Math.round((doneCount / total) * 100);

  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-5 lg:p-6" data-testid="process-timeline">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
        <div>
          <p className="overline text-emerald-700">Proceso del negocio</p>
          <h3 className="font-display text-lg font-bold tracking-tight">Estado actual</h3>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold tabular-nums" style={{ color: "#0a7a3b" }}>{pct}%</div>
          <p className="text-[11px] text-slate-500">{doneCount} de {total} etapas</p>
        </div>
      </div>

      <div className="w-full h-1.5 bg-zinc-100 rounded-full overflow-hidden mb-5">
        <div
          className="h-full rounded-full transition-all"
          style={{ width: `${pct}%`, background: "linear-gradient(90deg, #10b981, #059669)" }}
        />
      </div>

      <ol className="space-y-0 relative">
        {stages.map((s, idx) => {
          const Icon = ICONS[s.key] || CircleDashed;
          const style = STATUS_STYLES[s.status] || STATUS_STYLES.pending;
          const isLast = idx === stages.length - 1;
          return (
            <li key={s.key} className="flex gap-4 relative" data-testid={`stage-${s.key}`}>
              {/* vertical line behind dot */}
              {!isLast && (
                <div
                  className={`absolute left-[19px] top-10 bottom-0 w-[2px] ${style.line}`}
                  aria-hidden="true"
                />
              )}
              <div
                className={`relative w-10 h-10 rounded-full flex items-center justify-center shrink-0 ring-4 ${style.ring} ${style.dot} shadow-sm`}
              >
                {s.status === "done" ? <Check className="w-5 h-5" /> : s.status === "active" ? <Clock className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
              </div>
              <div className="flex-1 pb-6">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-display font-bold text-sm text-slate-900">{s.label}</p>
                  <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold border px-2 py-0.5 rounded-full ${style.badge}`}>
                    {style.badgeText}
                  </span>
                </div>
                {!compact && <p className="mt-1 text-xs text-slate-600 leading-relaxed">{s.description}</p>}
                <div className="mt-1.5 flex items-center gap-3 text-[11px] text-slate-500">
                  {s.at && <span className="tabular-nums">{fmtDateShortCL(s.at)}</span>}
                  {s.meta?.offer_amount && (
                    <span className="font-semibold text-emerald-700 tabular-nums">{fmtCLP(s.meta.offer_amount)}</span>
                  )}
                </div>
                {/* Admin quick approve/reject for pending document access request */}
                {s.key === "documents" && s.status === "active" && s.meta?.access_id && user?.role === "super_admin" && (
                  <div className="mt-3 flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-md">
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-amber-900">Solicitud pendiente</p>
                      <p className="text-[11px] text-amber-800 mt-0.5 line-clamp-2">
                        {s.meta?.justification || "El inversionista solicita acceso a los documentos."}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      className="btn-investment h-7 px-2 text-xs"
                      onClick={() => decide(s.meta.access_id, "approved")}
                      data-testid={`timeline-doc-approve`}
                    >
                      <Check className="w-3.5 h-3.5 mr-1" /> Sí
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 px-2 text-xs text-rose-700 border-rose-200 hover:bg-rose-50"
                      onClick={() => decide(s.meta.access_id, "rejected")}
                      data-testid={`timeline-doc-reject`}
                    >
                      <X className="w-3.5 h-3.5 mr-1" /> No
                    </Button>
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
